/* --------------------------------------------------------------------------
   home-aeo.js — predictive part-number search for every [data-amerex-search]
   form on the homepage (the hero and the parts finder share one snippet, so
   they share one behaviour). Loaded deferred on the index template only.

   Without JS the form still submits a normal GET to the search page, so the
   field degrades cleanly.
   -------------------------------------------------------------------------- */
(function () {
	'use strict';

	var SUGGEST_FIELDS = 'title,product_type,variants.title,variants.sku,vendor,tag';

	function esc(value) {
		return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
			return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
		});
	}

	function init(form) {
		if (!form || form.dataset.amerexReady === 'true') return;
		form.dataset.amerexReady = 'true';

		var input = form.querySelector('.amerex-search__input');
		var panel = form.querySelector('.amerex-search__results');
		var clear = form.querySelector('.amerex-search__clear');
		if (!input || !panel) return;

		var predictive = form.getAttribute('data-predictive') === 'true';
		var searchUrl = form.getAttribute('data-search-url') || '/search';
		var suggestUrl = form.getAttribute('data-suggest-url') || '/search/suggest';
		var optionPrefix = panel.id + '-opt-';
		var timer = null;
		var controller = null;
		var activeIndex = -1;

		function toggleClear() {
			if (!clear) return;
			if (input.value.length > 0) clear.removeAttribute('hidden');
			else clear.setAttribute('hidden', '');
		}

		function close() {
			panel.classList.remove('is-open');
			panel.innerHTML = '';
			input.setAttribute('aria-expanded', 'false');
			input.removeAttribute('aria-activedescendant');
			activeIndex = -1;
		}

		function open() {
			panel.classList.add('is-open');
			input.setAttribute('aria-expanded', 'true');
		}

		function thumb(url) {
			if (!url) return '<span class="amerex-search__thumb"></span>';
			var sep = url.indexOf('?') > -1 ? '&' : '?';
			return '<img class="amerex-search__thumb" src="' + esc(url + sep + 'width=120') +
				'" alt="" width="44" height="44" loading="lazy" decoding="async">';
		}

		function skuOf(product) {
			if (!product.variants || !product.variants.length) return '';
			for (var i = 0; i < product.variants.length; i++) {
				if (product.variants[i] && product.variants[i].sku) return product.variants[i].sku;
			}
			return '';
		}

		function allResultsHref(term) {
			return searchUrl + '?q=' + encodeURIComponent(term) + '&type=product&options%5Bprefix%5D=last';
		}

		function render(products, term) {
			var html = '';

			if (!products.length) {
				html += '<div class="amerex-search__empty">No matches for &ldquo;' + esc(term) +
					'&rdquo; yet &mdash; press Enter to run a full search.</div>';
			} else {
				for (var i = 0; i < products.length; i++) {
					var p = products[i];
					var sku = skuOf(p);
					var price = p.price ? '<span class="amerex-search__option-price">$' + esc(p.price) + '</span>' : '';

					html += '<a class="amerex-search__option" role="option" aria-selected="false" id="' + optionPrefix + i +
						'" href="' + esc(p.url) + '" tabindex="-1">' +
						thumb(p.featured_image && p.featured_image.url ? p.featured_image.url : p.image) +
						'<span class="amerex-search__option-text">' +
							'<span class="amerex-search__option-title">' + esc(p.title) + '</span>' +
							(sku ? '<span class="amerex-search__option-sku">' + esc(sku) + '</span>' : '') +
						'</span>' + price +
					'</a>';
				}
			}

			html += '<a class="amerex-search__all" role="option" aria-selected="false" id="' + optionPrefix + products.length +
				'" href="' + esc(allResultsHref(term)) + '" tabindex="-1">View all results for &ldquo;' + esc(term) + '&rdquo;</a>';

			panel.innerHTML = html;
			activeIndex = -1;
			input.removeAttribute('aria-activedescendant');
			open();
		}

		function search(term) {
			if (controller) controller.abort();
			controller = new AbortController();

			/* resources[options][fields] is required — without it the suggest
			   endpoint will not match on variant SKUs (the part numbers). */
			var url = suggestUrl + '.json?q=' + encodeURIComponent(term) +
				'&resources[type]=product' +
				'&resources[limit]=6' +
				'&resources[options][unavailable_products]=last' +
				'&resources[options][fields]=' + SUGGEST_FIELDS;

			fetch(url, { signal: controller.signal })
				.then(function (r) {
					if (!r.ok) throw new Error(r.status);
					return r.json();
				})
				.then(function (data) {
					var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
					render(products, term);
				})
				.catch(function () {
					/* aborted, offline or throttled — fall back to the form submit */
					close();
				});
		}

		function items() {
			return panel.querySelectorAll('.amerex-search__option, .amerex-search__all');
		}

		function setActive(next) {
			var list = items();
			if (!list.length) return;

			if (activeIndex > -1 && list[activeIndex]) {
				list[activeIndex].classList.remove('is-active');
				list[activeIndex].setAttribute('aria-selected', 'false');
			}

			activeIndex = next;
			if (activeIndex < 0) activeIndex = list.length - 1;
			if (activeIndex >= list.length) activeIndex = 0;

			var el = list[activeIndex];
			el.classList.add('is-active');
			el.setAttribute('aria-selected', 'true');
			el.scrollIntoView({ block: 'nearest' });
			input.setAttribute('aria-activedescendant', el.id);
		}

		input.addEventListener('input', function () {
			toggleClear();
			if (!predictive) return;

			var term = input.value.trim();
			clearTimeout(timer);

			if (term.length < 2) {
				close();
				return;
			}

			timer = setTimeout(function () { search(term); }, 200);
		});

		input.addEventListener('keydown', function (e) {
			if (e.key === 'Escape') {
				close();
				return;
			}

			var list = items();
			if (!panel.classList.contains('is-open') || !list.length) return;

			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setActive(activeIndex + 1);
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				setActive(activeIndex - 1);
			} else if (e.key === 'Enter' && activeIndex > -1) {
				e.preventDefault();
				window.location.href = list[activeIndex].getAttribute('href');
			}
		});

		if (clear) {
			clear.addEventListener('click', function () {
				input.value = '';
				toggleClear();
				close();
				input.focus();
			});
		}

		form.addEventListener('submit', function () {
			clearTimeout(timer);
			if (controller) controller.abort();
		});

		document.addEventListener('click', function (e) {
			if (!form.contains(e.target)) close();
		});
	}

	function initAll(root) {
		var scope = root && root.querySelectorAll ? root : document;
		var forms = scope.querySelectorAll('[data-amerex-search]');
		for (var i = 0; i < forms.length; i++) init(forms[i]);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () { initAll(document); });
	} else {
		initAll(document);
	}

	/* Theme editor re-renders a section's markup in place. */
	document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
